import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  FamilyGraphProvider,
  useFamilyGraph,
  GraphMember,
  Side,
  Relationship,
  type TreeRelationshipWorkflow,
} from '@/contexts/FamilyGraphContext';
import {
  resolveViewerAnchor,
} from '@/lib/familyPlacement';
import {
  resolveCanonicalTreeGenerationConsumer,
} from '@/governor/tree/CanonicalTreeGenerationConsumerAuthority';

import {
  resolveCanonicalTreeFamilyUnitIdentityProjection,
  resolveCanonicalTreeViewerPresentation,
} from '@/governor/tree/CanonicalTreeViewerPresentationAuthority';
import {
  projectCanonicalTreeCompatibilityEdges,
} from '@/governor/tree/CanonicalTreeCompatibilityEdgeProjection';
import {
  resolveCanonicalTreeGeometryRoles,
} from '@/governor/tree/CanonicalTreeGeometryRoleAuthority';
import {
  resolveCanonicalTreeBranchConsumer,
} from '@/governor/tree/CanonicalTreeBranchConsumerAuthority';
import {
  resolveCanonicalTreeLayoutPathRuntime,
} from '@/governor/tree/CanonicalTreeLayoutPathRuntimeSelector';
import {
  createTreeRuntimeBranchLayoutAuthoritySnapshot,
} from '@/governor/tree-runtime/TreeRuntimeBranchLayoutAuthoritySnapshot';
import {
  runCanonicalTreeTopologyReplacementSafetyGate,
} from '@/governor/tree/CanonicalTreeTopologyReplacementSafetyGate';
import {
  resolveCanonicalTreeActiveRuntimeAuthorityConvergence,
} from '@/governor/tree/CanonicalTreeActiveRuntimeAuthorityConvergenceContract';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/sonner';
import { DEV_BYPASS_TEST_MODE } from '@/lib/devBypass';




import AddGraphMemberModal from './AddGraphMemberModal';
import PendingApprovalsPanel from './PendingApprovalsPanel';
import FamilyTreeView, { TreeViewMode } from './FamilyTreeView';
import type {
  TreeRuntimeSelectorDiagnostics,
} from '@/governor/tree-runtime/TreeRuntimeSelectorDiagnostics';
import {
  classifyTreeRuntimeSelectorDiscrepancies,
} from '@/governor/tree-runtime/TreeRuntimeSelectorDiscrepancyClassification';
import {
  classifyTreeRuntimeSelectorDiscrepancySources,
} from '@/governor/tree-runtime/TreeRuntimeSelectorDiscrepancySourceClassification';
import {
  TREE_RUNTIME_DISCREPANCY_COHORT_LIFECYCLE_PROBE_FUNCTION,
} from '@/governor/tree-runtime/TreeRuntimeDiscrepancyCohortLifecycleProbeContract';
import {
  TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_RPC,
} from '@/governor/tree-runtime/TreeRuntimeMissingParentIdentityParityAuthority';
import {
  createTreeRuntimeMissingParentIdentityOwnerBlockedObservation,
  runTreeRuntimeMissingParentIdentityBrowserRuntime,
  type TreeRuntimeMissingParentIdentityBrowserObservation,
} from '@/governor/tree-runtime/TreeRuntimeMissingParentIdentityParityBrowserRuntime';
import {
  createTreeRuntimeMissingParentIdentityParityPublicationFingerprint,
} from '@/governor/tree-runtime/TreeRuntimeMissingParentIdentityParityPublicationContract';
import {
  planTreeRuntimeMissingParentRepairCandidates,
  type TreeRuntimeMissingParentRepairCandidatePlan,
} from '@/governor/tree-runtime/TreeRuntimeMissingParentRepairCandidatePlanningAuthority';
import {
  resolveTreeRuntimeMissingParentLifecycleAttribution,
  type TreeRuntimeLifecycleBrowserOwnerMode,
  type TreeRuntimeMissingParentLifecycleAttributionDecision,
} from '@/governor/tree-runtime/TreeRuntimeMissingParentLifecycleAttributionParity';
import {
  createTreeRuntimeMissingParentLifecycleAttributionPublicationFingerprint,
} from '@/governor/tree-runtime/TreeRuntimeMissingParentLifecycleAttributionPublicationContract';
import {
  publishTreeRuntimeMissingParentIdentityParityEvidence,
  publishTreeRuntimeMissingParentLifecycleAttributionEvidence,
} from '@/governor/tree-runtime/TreeRuntimeGovernorBridge';
import SmartSuggestions from './SmartSuggestions';
import ProfileDetailDrawer from './ProfileDetailDrawer';
import ErrorBoundary from '@/components/ErrorBoundary';

import MissingBranches from './MissingBranches';
import FamilyAchievements from './FamilyAchievements';
import PostSignupChecklist from './PostSignupChecklist';
import RelativeRoster from './RelativeRoster';
import SetRelationshipModal from './SetRelationshipModal';
import GraphDebugPanel from './GraphDebugPanel';


import { Button } from '@/components/ui/button';
import {
  Plus,
  Users,
  Sparkles,
  Trophy,
  Heart,
  GitBranch,
  CheckCircle2,
  Circle,
  Lock,
  ShieldCheck,
  Share2,
} from 'lucide-react';

/**
 * Invite handler signature — the dashboard can either pop the generic invite
 * modal (no opts) or pass a person/relation context for the "invite with
 * purpose" pre-filled-message flow.
 */
type InviteHandler = (opts?: { personName?: string | null; relation?: string | null; targetMemberId?: string | null }) => void;

interface MissionsProps {
  members: GraphMember[];
  memoriesCount: number;
  onAddSide: (side: Side, rel?: Relationship) => void;
  onInvite: InviteHandler;
}

const Missions: React.FC<MissionsProps> = ({ members, memoriesCount, onAddSide, onInvite }) => {
  const approved = members.filter((m) => m.approvalStatus === 'approved');
  const hasParents = approved.some((m) => m.relationship === 'mother' || m.relationship === 'father');
  const hasGrandparent = approved.some((m) => m.relationship === 'grandparent');
  const hasCousin = approved.some((m) => m.relationship === 'cousin');
  const hasMemory = memoriesCount > 0 || approved.some((m) => m.notes);
  const hasConfirmed = approved.length > 0;

  const missions = [
    {
      id: 'parents',
      label: 'Add your parents',
      done: hasParents,
      action: () => onAddSide('mother', 'mother'),
    },
    {
      id: 'grandparent',
      label: 'Add your grandparents',
      done: hasGrandparent,
      action: () => onAddSide('mother', 'grandparent'),
    },
    {
      id: 'cousin',
      label: 'Invite a cousin',
      done: hasCousin,
      action: () => onInvite({ relation: 'cousin' }),
    },
    { id: 'memory', label: 'Add a memory', done: hasMemory, action: () => onAddSide('mother') },
    {
      id: 'confirm',
      label: 'Confirm a relationship',
      done: hasConfirmed,
      action: () => onAddSide('father'),
    },
  ];

  const completed = missions.filter((m) => m.done).length;

  return (
    <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-amber-600" />
        <h3 className="font-serif font-bold text-lg text-amber-900">Family Missions</h3>
        <span className="ml-auto text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
          {completed}/{missions.length}
        </span>
      </div>
      <div className="space-y-2">
        {missions.map((m) => (
          <button
            key={m.id}
            onClick={m.action}
            disabled={m.done}
            className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition ${
              m.done
                ? 'bg-emerald-50 border border-emerald-200 cursor-default'
                : 'bg-white border border-amber-200 hover:bg-amber-50'
            }`}
          >
            {m.done ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-amber-400 shrink-0" />
            )}
            <span
              className={`text-sm font-medium ${
                m.done ? 'text-emerald-800 line-through' : 'text-slate-800'
              }`}
            >
              {m.label}
            </span>
            {!m.done && (
              <span className="ml-auto text-xs text-amber-700 font-semibold">Start →</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * ProgressScore — now built around an explicit "Your family is X% complete"
 * headline + a segmented breakdown of which buckets contribute (parents,
 * grandparents, siblings, children, extended). Score formula:
 *   - Parents added           (25 pts)
 *   - Grandparents added      (20 pts)
 *   - Siblings added          (15 pts)
 *   - Children added          (15 pts)
 *   - Extended relatives      (15 pts: aunts/uncles/cousins/great-grandparents)
 *   - Memories saved          (10 pts: at least 3 memories or notes)
 */
const ProgressScore: React.FC<{
  members: GraphMember[];
  memoriesCount: number;
  viewerMemberId: string | null;
}> = ({
  members,
  memoriesCount,
  viewerMemberId,
}) => {
  const approved = members.filter((m) => m.approvalStatus === 'approved');
  const pending = members.filter((m) => m.approvalStatus === 'pending').length;

  const segments = useMemo(() => {
    const hasParent = approved.some(
      (m) =>
        m.relationship === 'mother' ||
        m.relationship === 'father' ||
        m.relationship === 'step-parent' ||
        m.relationship === 'adopted-parent'
    );
    const parentCount = approved.filter(
      (m) =>
        m.relationship === 'mother' ||
        m.relationship === 'father' ||
        m.relationship === 'step-parent' ||
        m.relationship === 'adopted-parent'
    ).length;
    const grandparentCount = approved.filter((m) => m.relationship === 'grandparent').length;
    const siblingCount = approved.filter((m) => m.relationship === 'sibling').length;
    const childCount = approved.filter((m) => m.relationship === 'child').length;
    const extendedCount = approved.filter(
      (m) =>
        m.relationship === 'aunt-uncle' ||
        m.relationship === 'cousin' ||
        m.relationship === 'cousin-child' ||
        m.relationship === 'great-grandparent'
    ).length;
    const memoryPoints = memoriesCount + approved.filter((m) => !!m.notes).length;

    return [
      {
        id: 'parents',
        label: 'Parents',
        max: 25,
        // 1 parent → 12, 2 parents → 25 (smooth ramp)
        value: Math.min(parentCount / 2, 1) * 25,
        unlocked: hasParent,
        tone: 'from-pink-500 to-rose-500',
      },
      {
        id: 'grandparents',
        label: 'Grandparents',
        max: 20,
        value: Math.min(grandparentCount / 4, 1) * 20,
        unlocked: grandparentCount > 0,
        tone: 'from-fuchsia-500 to-purple-500',
      },
      {
        id: 'siblings',
        label: 'Siblings',
        max: 15,
        value: Math.min(siblingCount / 2, 1) * 15,
        unlocked: siblingCount > 0,
        tone: 'from-emerald-500 to-teal-500',
      },
      {
        id: 'children',
        label: 'Children',
        max: 15,
        value: Math.min(childCount / 2, 1) * 15,
        unlocked: childCount > 0,
        tone: 'from-rose-500 to-pink-500',
      },
      {
        id: 'extended',
        label: 'Extended family',
        max: 15,
        value: Math.min(extendedCount / 4, 1) * 15,
        unlocked: extendedCount > 0,
        tone: 'from-amber-500 to-orange-500',
      },
      {
        id: 'memories',
        label: 'Memories',
        max: 10,
        value: Math.min(memoryPoints / 3, 1) * 10,
        unlocked: memoryPoints > 0,
        tone: 'from-indigo-500 to-blue-500',
      },
    ];
  }, [approved, memoriesCount]);

  const completionScore = Math.round(segments.reduce((acc, s) => acc + s.value, 0));

  const generationConsumer = useMemo(
    () =>
      resolveCanonicalTreeGenerationConsumer({
        members,
        viewerId: viewerMemberId,
      }),
    [members, viewerMemberId],
  );

  const generations =
    generationConsumer.generationCount;

  const stats = [
    { label: 'Members added', value: approved.length, icon: Users, tone: 'text-fuchsia-600 bg-fuchsia-50' },
    { label: 'Generations reached', value: generations, icon: GitBranch, tone: 'text-purple-600 bg-purple-50' },
    { label: 'Pending confirmations', value: pending, icon: Trophy, tone: 'text-amber-600 bg-amber-50' },
    {
      label: 'Memories saved',
      value: memoriesCount + approved.filter((m) => !!m.notes).length,
      icon: Heart,
      tone: 'text-rose-600 bg-rose-50',
    },
  ];

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-gradient-to-r from-[#7B3FF2] via-[#FF4DA6] to-[#D4AF37] p-5 text-white shadow-md">
        <div className="flex items-center gap-4">
          <div className="relative w-20 h-20 shrink-0">
            <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
              <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="#fff"
                strokeWidth="3"
                strokeDasharray={`${(completionScore / 100) * 94.2} 94.2`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-serif font-extrabold text-xl">
              {completionScore}%
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest opacity-90 font-bold">
              Family Legacy Score
            </div>
            <div className="font-serif text-xl sm:text-2xl font-bold mt-0.5">
              Your family legacy is taking shape
            </div>
            <div className="text-xs opacity-90 mt-0.5">
              {completionScore < 30
                ? 'Just getting started — add a parent to anchor your tree.'
                : completionScore < 60
                ? 'Branching out beautifully — keep filling in the gaps.'
                : completionScore < 85
                ? 'A flourishing family graph — invite kin to verify branches.'
                : 'A living legacy for every generation.'}
            </div>
          </div>
        </div>

        {/* Segment breakdown — small bars showing which buckets contribute. */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {segments.map((s) => {
            const pct = Math.round((s.value / s.max) * 100);
            return (
              <div key={s.id} className="bg-white/15 rounded-lg px-2.5 py-1.5">
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wide opacity-95">
                  <span className="truncate">{s.label}</span>
                  <span className="tabular-nums">{pct}%</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-white/20 overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${s.tone}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm"
          >
            <div className={`inline-flex w-9 h-9 rounded-full items-center justify-center ${s.tone}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div className="mt-2 text-2xl font-bold text-slate-900">{s.value}</div>
            <div className="text-xs text-slate-600">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SignedOutPlaceholder: React.FC = () => (
  <section id="graph" className="py-16 bg-gradient-to-br from-[#F8F0FF] via-[#F3E8FF] to-[#FFF4D8]">
    <div className="max-w-3xl mx-auto px-4 text-center">
      <div className="inline-flex w-14 h-14 rounded-full bg-gradient-to-br from-[#7B3FF2] via-[#FF4DA6] to-[#D4AF37] items-center justify-center text-white mb-4 shadow-lg">
        <Lock className="w-7 h-7" />
      </div>
      <h2 className="font-serif text-3xl font-bold text-slate-900 mb-2">
        Your Living Family Tree
      </h2>
      <p className="text-slate-700 mb-4">
        Sign in to start building a dynamic family tree centered on you, with branches for both sides
        of your family.
      </p>
    </div>
  </section>
);

const Inner: React.FC<{ onInvite: InviteHandler; onShare: () => void }> = ({ onInvite, onShare }) => {
  const { user, isStaff, memberName } = useAuth();
  const {
    members,
    memories,
    loading,
    isBulkWriting,
    addMember,
    claimProfile,
    updateMember,
    deleteMember,
    refresh,
    viewerMemberId,
    effectiveOwnerUserId,
  } = useFamilyGraph();

  /*
   * CC-TREE-LIVE-003G2M
   *
   * Internal-only lifecycle attribution observation.
   * No UI, Governor publication, selector authority,
   * repair authority, or family-record mutation consumes
   * this value in 003G2M.
   */
  const canonicalMissingParentLifecycleAttributionRef =
    useRef<
      TreeRuntimeMissingParentLifecycleAttributionDecision | null
    >(null);


  /*
   * CC-TREE-LIVE-003G2R
   *
   * Private identity-parity browser observation.
   * R does not publish this value to Governor evidence,
   * UI, repair authority, or any family-record writer.
   */
  const canonicalMissingParentIdentityParityObservationRef =
    useRef<
      TreeRuntimeMissingParentIdentityBrowserObservation | null
    >(null);

  /*
   * CC-TREE-LIVE-003G2T
   *
   * Private repair-candidate planning derived only from
   * a completed R identity-parity decision and the same
   * canonical unresolved compatibility evidence.
   *
   * Candidate identities remain browser-memory private.
   * T has no publication, UI, RPC, or mutation authority.
   */
  const canonicalMissingParentRepairCandidatePlanRef =
    useRef<
      TreeRuntimeMissingParentRepairCandidatePlan | null
    >(null);

  /*
   * CC-TREE-LIVE-003G2S
   *
   * Identity-parity publication has independent dedupe
   * authority from the historical N lifecycle publication.
   *
   * Actor/effective-owner identifiers remain browser-memory
   * scope only and are never included in published evidence.
   */
  const canonicalMissingParentIdentityParityPublicationScopeRef =
    useRef<string | null>(null);

  const canonicalMissingParentIdentityParityPublishedFingerprintRef =
    useRef<string | null>(null);


  /*
   * CC-TREE-LIVE-003G2N
   *
   * Private publication dedupe state.
   * The scope may contain actor/effective-owner identifiers
   * in browser memory only. Those identifiers are never
   * serialized into Governor or Runtime Evidence.
   */
  const canonicalMissingParentLifecyclePublicationScopeRef =
    useRef<string | null>(null);

  const canonicalMissingParentLifecyclePublishedFingerprintRef =
    useRef<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);

  /*
   * Every Add Member transaction receives a fresh component
   * identity. This prevents unsaved internal modal state from
   * leaking across separate authoring attempts without granting
   * this presentation milestone any Tree-record mutation authority.
   */
  const [
    addAuthoringSession,
    setAddAuthoringSession,
  ] = useState(0);

  const [defaultSide, setDefaultSide] = useState<Side>('mother');
  const [defaultRel, setDefaultRel] = useState<Relationship | undefined>(undefined);
  // CONTEXT-AWARE PARENT — id of the node the user tapped "+" from. Passed
  // straight to AddGraphMemberModal as `defaultParentId` so the new
  // 'child' insert uses the tapped node as parent_id (rule: "the parent
  // is ALWAYS the node the user tapped from"). Cleared on close.
  const [defaultParentId, setDefaultParentId] = useState<string | null>(null);

  // Currently-open profile in the detail drawer.
  const [selected, setSelected] = useState<GraphMember | null>(null);

  // Tracks which primary action the drawer should open with — e.g. the user
  // tapped "Add Memory" from the tree's quick-action menu, so we focus the
  // memory composer when the drawer mounts.
  const [selectedAction, setSelectedAction] = useState<
    'profile' | 'memory' | 'photo' | 'video' | undefined
  >(undefined);

  // When set, the tree visually re-centers on this person so the user can
  // explore their branch. Cleared by tapping "Back to my tree" or recenter.
  const [centeredMemberId, setCenteredMemberId] = useState<string | null>(null);
  // True while we're materializing the self-anchor row (first time the user
  // taps the "You" card). Prevents double-clicks from creating two rows.
  const [resolvingSelf, setResolvingSelf] = useState(false);

  // Visual filter for which side of the tree to display. Purely presentation —
  // never modifies any data. Defaults to 'full' (everyone).
  const [treeViewMode, setTreeViewMode] = useState<TreeViewMode>('full');

  // The relative currently being re-connected / re-placed via the roster's
  // "Connect to Tree" / "Set Relationship" buttons.
  const [relationshipTarget, setRelationshipTarget] = useState<GraphMember | null>(null);


  // Count of invites this user has sent — drives the PostSignupChecklist
  // and the "Branch Connector" achievement. Refreshed when the invites
  // table changes (best-effort: poll every 30s and refetch on focus).
  const [invitesSent, setInvitesSent] = useState<number>(0);

  // Track member IDs already-seen so we can detect when a NEW claimed /
  // joined member shows up (someone accepted an invite, claimed a profile,
  // etc.) and surface a celebratory "Your family just grew" toast.
  const seenClaimedIds = useRef<Set<string>>(new Set());
  const seenIdsBootstrapped = useRef(false);

  // One-shot guard so we only auto-merge self-anchor duplicates ONCE per
  // session per member-set signature — prevents an infinite refresh loop
  // (merge → refresh → members change → merge again).
  const autoMergedSignatures = useRef<Set<string>>(new Set());
  const autoMergeRunning = useRef(false);

  // True when the logged-in user is viewing a SHARED family tree they don't
  // own (e.g. they accepted an invite + claimed their placeholder, so the
  // graph being loaded belongs to a relative). In that case we MUST not
  // run the auto-merge logic that consolidates side='self' rows owned by
  // the user — those rows live in someone else's graph and the merge logic
  // would either fail under RLS or merge profiles that aren't actually
  // duplicates of the viewer.
  const isInSharedGraph = !!user?.id && !!effectiveOwnerUserId && effectiveOwnerUserId !== user.id;

  // Resolve the canonical "viewer" member — the row inside the loaded graph
  // that represents the currently-logged-in user. In a shared tree this is
  // simply the viewerMemberId from context (the row claimed by them in
  // their relative's graph). In the legacy single-user case we fall back
  // to the first claimed self-anchor we created on their own graph.
  const viewerMember = useMemo<GraphMember | null>(() => {
    if (viewerMemberId) {
      return members.find((m) => m.id === viewerMemberId) ?? null;
    }
    if (!user?.id) return null;
    return (
      members.find((m) => m.claimedByUserId === user.id) ??
      members.find(
        (m) => m.side === 'self' && m.ownerUserId === user.id && m.relationship === 'unknown'
      ) ??
      null
    );
  }, [members, viewerMemberId, user?.id]);

  /*
   * Canonical viewer presentation authority:
   *
   * - Bloodline and modern-family canonical paths are Tree-visible.
   * - Branch authorization does not redefine topology.
   * - Saved rows with no canonical viewer path remain in Needs connection.
   * - Structural rows remain the complete immutable graph.
   */
  const canonicalCompatibility = useMemo(
    () =>
      projectCanonicalTreeCompatibilityEdges({
        members,
      }),
    [members],
  );

  /*
   * CC-TREE-LIVE-003G3T
   *
   * Canonical family-unit identity is derived upstream from the
   * complete loaded graph. The renderer receives certified identity
   * and must never reconstruct parentage from color or geometry.
   */
  const canonicalFamilyUnitIdentity = useMemo(
    () =>
      resolveCanonicalTreeFamilyUnitIdentityProjection({
        memberIds:
          members.map(
            member => member.id,
          ),
        edges:
          canonicalCompatibility.edges,
        unresolvedEvidence:
          canonicalCompatibility.unresolvedEvidence,
        invalidEvidence:
          canonicalCompatibility.invalidEvidence,
      }),
    [
      members,
      canonicalCompatibility,
    ],
  );

  const canonicalViewerPresentation = useMemo(
    () =>
      resolveCanonicalTreeViewerPresentation({
        members,
        viewerId:
          viewerMember?.id ?? null,
        centeredMemberId:
          centeredMemberId ??
          viewerMember?.id ??
          null,
        edges:
          canonicalCompatibility.edges,
      }),
    [
      members,
      viewerMember?.id,
      centeredMemberId,
      canonicalCompatibility,
    ],
  );

  const viewerProjectionMap =
    canonicalViewerPresentation
      .projection
      ?.projections ??
    new Map();

  const canonicalTreeMembers =
    canonicalViewerPresentation.treeMembers;

  const canonicalGeometryRoles = useMemo(
    () =>
      resolveCanonicalTreeGeometryRoles({
        projection:
          canonicalViewerPresentation.projection,
        treeVisibleMemberIds:
          canonicalViewerPresentation.reachableMemberIds,
        edges:
          canonicalViewerPresentation.edges,
      }),
    [canonicalViewerPresentation],
  );

  /*
   * CC-TREE-LIVE-003G2B
   *
   * Assemble active runtime convergence evidence once at the
   * canonical presentation boundary.
   *
   * FamilyTreeView receives the same immutable branch/layout authority
   * used by convergence evidence and rendered geometry.
   */
  const canonicalRuntimeAuthority = useMemo(() => {
    const resolvedViewerMemberId =
      viewerMember?.id ?? null;

    const branch =
      resolveCanonicalTreeBranchConsumer({
        members,
        viewerId:
          resolvedViewerMemberId,
      });

    const layoutPath =
      resolveCanonicalTreeLayoutPathRuntime({
        members,
        viewerId:
          resolvedViewerMemberId,
      });

    const branchLayoutAuthority =
      createTreeRuntimeBranchLayoutAuthoritySnapshot({
        viewerMemberId:
          resolvedViewerMemberId,
        treeVisibleMemberIds:
          canonicalViewerPresentation
            .reachableMemberIds,

        presentationHasProjection:
          canonicalViewerPresentation
            .projection !== null,

        presentationUnresolvedEvidenceCount:
          canonicalViewerPresentation
            .unresolvedEvidenceCount,

        presentationConflictingEvidenceCount:
          canonicalViewerPresentation
            .conflictingEvidenceCount,

        presentationInvalidCompatibilityEvidenceCount:
          canonicalViewerPresentation
            .invalidCompatibilityEvidenceCount,

        geometryRoleMemberIds:
          new Set(
            canonicalGeometryRoles
              .roleByMemberId.keys(),
          ),

        geometryMissingProjectionMemberIdsCount:
          canonicalGeometryRoles
            .missingProjectionMemberIds.length,

        branch,
        layoutPath,
      });

    const topology =
      runCanonicalTreeTopologyReplacementSafetyGate({
        members,
        viewerMemberId:
          resolvedViewerMemberId,
      });

    const convergence =
      resolveCanonicalTreeActiveRuntimeAuthorityConvergence({
      presentation: {
        hasProjection:
          canonicalViewerPresentation.projection !== null,
        reachableMemberIds:
          canonicalViewerPresentation.reachableMemberIds,
        treeMemberIds:
          new Set(
            canonicalViewerPresentation.treeMembers.map(
              member => member.id,
            ),
          ),
        unresolvedEvidenceCount:
          canonicalViewerPresentation.unresolvedEvidenceCount,
        conflictingEvidenceCount:
          canonicalViewerPresentation.conflictingEvidenceCount,
        invalidCompatibilityEvidenceCount:
          canonicalViewerPresentation
            .invalidCompatibilityEvidenceCount,
      },
      geometry:
        canonicalGeometryRoles,
      branch: {
        source:
          branchLayoutAuthority
            .branch.source,
        readiness:
          branchLayoutAuthority
            .branch.migrationReadiness,
      },
      layoutPath: {
        source:
          branchLayoutAuthority
            .layoutPath.source,
        readiness:
          branchLayoutAuthority
            .layoutPath.readiness,
      },
      topology: {
        selectedSource:
          topology.selectedSource,
        readiness:
          topology.readiness,
      },
      canonicalEdgeIds:
        new Set(
          canonicalViewerPresentation.edges.map(
            edge => edge.id,
          ),
        ),
    });

    const selectorDiagnostics:
      TreeRuntimeSelectorDiagnostics =
      Object.freeze({
        presentationUnresolvedCount:
          canonicalViewerPresentation
            .unresolvedEvidenceCount,
        presentationConflictingCount:
          canonicalViewerPresentation
            .conflictingEvidenceCount,
        presentationInvalidCompatibilityCount:
          canonicalViewerPresentation
            .invalidCompatibilityEvidenceCount,

        branchCanonicalAdditionCount:
          branch.canonicalAdditionCount,
        branchUnresolvedCount:
          branch.unresolvedEvidenceCount,
        branchConflictingCount:
          branch.conflictingEvidenceCount,

        layoutParityCount:
          layoutPath.parityCount,
        layoutCanonicalAdditionCount:
          layoutPath.canonicalAdditionCount,
        layoutLegacyOnlyCount:
          layoutPath.legacyOnlyCount,
        layoutMismatchCount:
          layoutPath.mismatchCount,
        layoutUnresolvedCount:
          layoutPath.unresolvedEvidenceCount,
        layoutConflictingCount:
          layoutPath.conflictingEvidenceCount,
        layoutInvalidCompatibilityCount:
          layoutPath
            .invalidCompatibilityEvidenceCount,

        topologyParityCount:
          topology.summary.parityCount,
        topologyLegacyOnlyCount:
          topology.summary.legacyOnlyCount,
        topologyCanonicalOnlyCount:
          topology.summary.canonicalOnlyCount,
        topologyGenerationMismatchCount:
          topology.summary
            .generationMismatchCount,
        topologyBranchPathMismatchCount:
          topology.summary
            .branchPathMismatchCount,
        topologyUnresolvedCount:
          topology.summary.unresolvedCount,

        topologyCompatibilityUnresolvedCount:
          topology.compatibilityUnresolvedCount,
        topologyCompatibilityInvalidCount:
          topology.compatibilityInvalidCount,
        topologyCanonicalUnresolvedCount:
          topology.canonicalUnresolvedCount,
        topologyCanonicalConflictingCount:
          topology.canonicalConflictingCount,
        topologyLayoutPathMismatchCount:
          topology.layoutPathMismatchCount,
        topologyLayoutPathLegacyOnlyCount:
          topology.layoutPathLegacyOnlyCount,
      });

    const canonicalProjections =
      canonicalViewerPresentation
        .projection
        ?.projections ??
      new Map();

    const selectorDiscrepancyClassification =
      classifyTreeRuntimeSelectorDiscrepancies({
        canonicalProjections,
        topologyMembers:
          topology.members,
        unresolvedCompatibilityEvidence:
          canonicalCompatibility
            .unresolvedEvidence,
        invalidCompatibilityEvidence:
          canonicalCompatibility
            .invalidEvidence,
      });

    const selectorDiscrepancySourceClassification =
      classifyTreeRuntimeSelectorDiscrepancySources({
        canonicalProjections,
        topologyMembers:
          topology.members,
        canonicalEdges:
          canonicalViewerPresentation.edges,
        unresolvedCompatibilityEvidence:
          canonicalCompatibility
            .unresolvedEvidence,
      });

    return Object.freeze({
      branchLayoutAuthority,
      convergence,
      selectorDiagnostics,
      selectorDiscrepancyClassification,
      selectorDiscrepancySourceClassification,
    });
  }, [
    members,
    viewerMember?.id,
    canonicalViewerPresentation,
    canonicalGeometryRoles,
    canonicalCompatibility,
  ]);

  const canonicalBranchLayoutAuthority =
    canonicalRuntimeAuthority
      .branchLayoutAuthority;

  const canonicalRuntimeConvergence =
    canonicalRuntimeAuthority.convergence;

  const canonicalSelectorDiagnostics =
    canonicalRuntimeAuthority.selectorDiagnostics;

  const canonicalSelectorDiscrepancyClassification =
    canonicalRuntimeAuthority
      .selectorDiscrepancyClassification;

  const canonicalSelectorDiscrepancySourceClassification =
    canonicalRuntimeAuthority
      .selectorDiscrepancySourceClassification;

  /*
   * CC-TREE-LIVE-003G2M
   *
   * Attribute the server-owned lifecycle counts to the
   * browser-owned missing-parent-reference cohort only when:
   *
   * - authentication is established;
   * - effective-graph hydration is complete;
   * - no Tree write is in flight;
   * - browser/server effective-owner modes are compatible;
   * - the 003G2L response is structurally valid;
   * - the aggregate lifecycle total equals the browser cohort.
   *
   * Aggregate equality never proves UUID-set identity.
   */
  useEffect(() => {
    let cancelled = false;

    /*
     * Reset publication dedupe only when the authenticated
     * actor/effective-owner context actually changes.
     *
     * loading/isBulkWriting alone never clears this state.
     */
    if (!user?.id) {
      canonicalMissingParentLifecyclePublicationScopeRef.current =
        null;

      canonicalMissingParentLifecyclePublishedFingerprintRef.current =
        null;

      canonicalMissingParentIdentityParityPublicationScopeRef.current =
        null;

      canonicalMissingParentIdentityParityPublishedFingerprintRef.current =
        null;
    } else if (
      !loading &&
      effectiveOwnerUserId
    ) {
      const publicationScope =
        JSON.stringify([
          user.id,
          effectiveOwnerUserId,
        ]);

      if (
        canonicalMissingParentLifecyclePublicationScopeRef.current !==
          publicationScope
      ) {
        canonicalMissingParentLifecyclePublicationScopeRef.current =
          publicationScope;

        canonicalMissingParentLifecyclePublishedFingerprintRef.current =
          null;
      }

      if (
        canonicalMissingParentIdentityParityPublicationScopeRef.current !==
          publicationScope
      ) {
        canonicalMissingParentIdentityParityPublicationScopeRef.current =
          publicationScope;

        canonicalMissingParentIdentityParityPublishedFingerprintRef.current =
          null;
      }
    }

    if (
      !user?.id ||
      loading ||
      isBulkWriting ||
      !effectiveOwnerUserId
    ) {
      canonicalMissingParentLifecycleAttributionRef.current =
        null;

      canonicalMissingParentIdentityParityObservationRef.current =
        null;

      canonicalMissingParentRepairCandidatePlanRef.current =
        null;

      return () => {
        cancelled = true;
      };
    }

    const browserCount =
      canonicalSelectorDiscrepancySourceClassification
        .missingReferenceParentIdsCount;


    /*
     * 003G2N publishes only completed 003G2M decisions.
     * Duplicate state inside the same actor/effective-owner
     * scope is suppressed before reaching the unbounded
     * Governor evidence bus.
     */
    const assignMissingParentLifecycleDecision =
      (
        decision:
          TreeRuntimeMissingParentLifecycleAttributionDecision,
      ): void => {
        canonicalMissingParentLifecycleAttributionRef.current =
          decision;

        const fingerprint =
          createTreeRuntimeMissingParentLifecycleAttributionPublicationFingerprint(
            decision,
          );

        if (
          canonicalMissingParentLifecyclePublishedFingerprintRef.current ===
            fingerprint
        ) {
          return;
        }

        const publication =
          publishTreeRuntimeMissingParentLifecycleAttributionEvidence({
            authenticatedSession: true,
            decision,
            source:
              'FamilyGraphSection',
          });

        if (publication.published) {
          canonicalMissingParentLifecyclePublishedFingerprintRef.current =
            fingerprint;
        }
      };

    /*
     * CC-TREE-LIVE-003G2S
     *
     * All R observations pass through one publication gate.
     * Fingerprinting occurs only on the sanitized S projection.
     *
     * Failed publication does not consume the fingerprint,
     * allowing a later identical observation to retry.
     */
    const assignMissingParentIdentityParityObservation =
      (
        observation:
          TreeRuntimeMissingParentIdentityBrowserObservation,
      ): void => {
        canonicalMissingParentIdentityParityObservationRef.current =
          observation;

        canonicalMissingParentRepairCandidatePlanRef.current =
          observation.decision
            ? planTreeRuntimeMissingParentRepairCandidates({
                evidence:
                  canonicalCompatibility.unresolvedEvidence,
                decision:
                  observation.decision,
              })
            : null;

        const fingerprint =
          createTreeRuntimeMissingParentIdentityParityPublicationFingerprint(
            observation,
          );

        if (
          fingerprint === 'INVALID_PUBLICATION' ||
          canonicalMissingParentIdentityParityPublishedFingerprintRef.current ===
            fingerprint
        ) {
          return;
        }

        const publication =
          publishTreeRuntimeMissingParentIdentityParityEvidence({
            authenticatedSession: true,
            observation,
            source:
              'FamilyGraphSection',
          });

        if (publication.published) {
          canonicalMissingParentIdentityParityPublishedFingerprintRef.current =
            fingerprint;
        }
      };

    /*
     * Browser inviter protection is intentionally broader
     * than the 003G2L RLS-bound server owner resolver.
     *
     * Reject locally. Do not issue an RPC whose cohort
     * cannot represent the loaded browser graph.
     */
    if (
      !viewerMemberId &&
      effectiveOwnerUserId !== user.id
    ) {
      assignMissingParentIdentityParityObservation(
        createTreeRuntimeMissingParentIdentityOwnerBlockedObservation(),
      );

      assignMissingParentLifecycleDecision(
        resolveTreeRuntimeMissingParentLifecycleAttribution({
          browserMissingReferenceParentIdsCount:
            browserCount,
          browserOwnerResolutionMode:
            'INVITER_PROTECTION',
          probeResponse:
            null,
        }),
      );

      return () => {
        cancelled = true;
      };
    }

    canonicalMissingParentLifecycleAttributionRef.current =
      null;

    canonicalMissingParentIdentityParityObservationRef.current =
      null;

    canonicalMissingParentRepairCandidatePlanRef.current =
      null;

    void (async () => {
      let browserOwnerResolutionMode:
        TreeRuntimeLifecycleBrowserOwnerMode =
        'INDETERMINATE';

      try {
        const {
          data: claimRows,
          error: claimError,
        } =
          await supabase
            .from('family_graph_members')
            .select('owner_user_id')
            .eq('claimed_by_user_id', user.id)
            .is('deleted_at', null);

        if (cancelled) {
          return;
        }

        if (claimError) {
          browserOwnerResolutionMode =
            'INDETERMINATE';
        } else {
          const claimOwnerIds =
            new Set(
              (claimRows ?? [])
                .map(
                  row =>
                    (row as {
                      owner_user_id?: string | null;
                    }).owner_user_id,
                )
                .filter(
                  (
                    ownerId,
                  ): ownerId is string =>
                    typeof ownerId === 'string' &&
                    ownerId.length > 0,
                ),
            );

          if (claimOwnerIds.size > 1) {
            /*
             * 003G2L uses LIMIT 1 with no ordering when
             * resolving an active claimed graph.
             * Browser multi-claim selection uses a
             * largest-graph rule, so parity is unprovable.
             */
            browserOwnerResolutionMode =
              'MULTI_CLAIM_AMBIGUOUS';
          } else if (
            claimOwnerIds.size === 1 &&
            viewerMemberId &&
            claimOwnerIds.has(
              effectiveOwnerUserId,
            )
          ) {
            browserOwnerResolutionMode =
              'ACTIVE_CLAIM_SINGLE_OWNER';
          } else if (
            claimOwnerIds.size === 0 &&
            !viewerMemberId &&
            effectiveOwnerUserId === user.id
          ) {
            browserOwnerResolutionMode =
              'SELF_FALLBACK';
          }
        }
      } catch {
        browserOwnerResolutionMode =
          'INDETERMINATE';
      }

      if (cancelled) {
        return;
      }

      if (
        browserOwnerResolutionMode !==
          'ACTIVE_CLAIM_SINGLE_OWNER' &&
        browserOwnerResolutionMode !==
          'SELF_FALLBACK'
      ) {
        assignMissingParentIdentityParityObservation(
          createTreeRuntimeMissingParentIdentityOwnerBlockedObservation(),
        );

        assignMissingParentLifecycleDecision(
          resolveTreeRuntimeMissingParentLifecycleAttribution({
            browserMissingReferenceParentIdsCount:
              browserCount,
            browserOwnerResolutionMode,
            probeResponse:
              null,
          }),
        );

        return;
      }

      /*
       * CC-TREE-LIVE-003G2R
       *
       * Identity parity runs beside the historical L/M path.
       * It derives its cohort only from canonical compatibility
       * evidence and sends only nonce + commitment to the P RPC.
       *
       * It never delays, changes, or publishes through M/N/O.
       */
      void runTreeRuntimeMissingParentIdentityBrowserRuntime({
        evidence:
          canonicalCompatibility.unresolvedEvidence,
        invokeRpc:
          async request => {
            const {
              data,
              error,
            } =
              await supabase.rpc(
                TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_RPC,
                request,
              );

            if (error) {
              throw error;
            }

            return data;
          },
      }).then(
        observation => {
          if (!cancelled) {
            assignMissingParentIdentityParityObservation(
              observation,
            );
          }
        },
      );

      let probeResponse:
        unknown = null;

      try {
        const {
          data,
          error,
        } =
          await supabase.rpc(
            TREE_RUNTIME_DISCREPANCY_COHORT_LIFECYCLE_PROBE_FUNCTION,
          );

        probeResponse =
          error
            ? null
            : data;
      } catch {
        probeResponse =
          null;
      }

      if (cancelled) {
        return;
      }

      assignMissingParentLifecycleDecision(
        resolveTreeRuntimeMissingParentLifecycleAttribution({
          browserMissingReferenceParentIdsCount:
            browserCount,
          browserOwnerResolutionMode,
          probeResponse,
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [
    user?.id,
    loading,
    isBulkWriting,
    effectiveOwnerUserId,
    viewerMemberId,
    members,
    canonicalSelectorDiscrepancySourceClassification
      .missingReferenceParentIdsCount,
    canonicalCompatibility.unresolvedEvidence,
  ]);

  const rosterMembers =
    canonicalViewerPresentation.rosterMembers;

  const isTreeVisibilityFiltered =
    !!viewerMember?.id &&
    canonicalTreeMembers.length < members.length;

  // Resolve the canonical "YOU" row from the loaded members list. Order:
  //   1. claimed_by_user_id === current user
  //   2. side === 'self' AND has a photoUrl (preserves photo even if claim
  //      flag was lost on a legacy row)
  //   3. side === 'self' AND owned by current user (oldest first)
  // Returns the row + every duplicate self-row that should be merged into it.
  //
  // CRITICAL BUG FIX — never merge real relatives into the YOU anchor.
  // A "self-candidate" must be either a CLAIMED row owned by the viewer
  // OR a true self-anchor placeholder (side='self' AND relationship is one
  // of the self-anchor markers: 'unknown' or literal 'self'). Without the
  // relationship filter, ANY relative the user adds with side='self' (a
  // sibling, spouse, child, parent, cousin, niece/nephew, anyone) was
  // being scooped into `duplicates` and then DELETED by the former
  // automatic self-merge path — which is exactly the bug where "adding
  // or saving any family member immediately removes them".
  // We now require the row to look like a self-anchor placeholder (or be
  // a claim-row) before it can ever be considered a merge target. Real
  // relatives with side='self' and a meaningful relationship label are
  // NEVER touched.
  const findSelfCandidates = (): { canonical: GraphMember | null; duplicates: GraphMember[] } => {
    if (!user?.id) return { canonical: null, duplicates: [] };
    const isSelfAnchorPlaceholder = (m: GraphMember) =>
      m.side === 'self' &&
      m.ownerUserId === user.id &&
      // Only the literal placeholder relationship labels count as
      // "this row IS the YOU anchor". Any other relationship means
      // it's a real relative the user added on their own side and
      // must be preserved.
      (m.relationship === 'unknown' || (m.relationship as string) === 'self');
    const candidates = members.filter(
      (m) => m.claimedByUserId === user.id || isSelfAnchorPlaceholder(m)
    );
    if (candidates.length === 0) return { canonical: null, duplicates: [] };
    const sorted = [...candidates].sort((a, b) => {
      const aClaimed = a.claimedByUserId === user.id ? 0 : 1;
      const bClaimed = b.claimedByUserId === user.id ? 0 : 1;
      if (aClaimed !== bClaimed) return aClaimed - bClaimed;
      const aHasPhoto = a.photoUrl ? 0 : 1;
      const bHasPhoto = b.photoUrl ? 0 : 1;
      if (aHasPhoto !== bHasPhoto) return aHasPhoto - bHasPhoto;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
    return { canonical: sorted[0], duplicates: sorted.slice(1) };
  };


  // Best display name for the YOU card. When the viewer is a claimed
  // member inside a shared graph, prefer THAT row's name (it's their
  // canonical profile in this family) — otherwise fall back to auth.
  const youName =
    viewerMember?.name?.trim() ||
    memberName ||
    user?.email?.split('@')[0] ||
    'You';

  // Photo of the canonical "YOU" row, used to render the YOU card on the
  // tree with the user's actual avatar instead of a generic gradient.
  const youPhotoUrl = useMemo(() => {
    if (viewerMember?.photoUrl) return viewerMember.photoUrl;
    const { canonical } = findSelfCandidates();
    return canonical?.photoUrl ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, user?.id, viewerMember?.photoUrl]);


  // Optional custom display label (e.g. "Godmother", "Mother-in-law",
  // "Second Cousin") collected from the QuickRelativePicker. Persisted
  // to relationship_override on the new row — pure display tweak, never
  // affects placement or kinship logic.
  const [defaultRelOverride, setDefaultRelOverride] = useState<string | null>(null);

  const [
    defaultRelationshipWorkflow,
    setDefaultRelationshipWorkflow,
  ] = useState<TreeRelationshipWorkflow | null>(null);

  const resetAddAuthoringDefaults = () => {
    setDefaultSide('mother');
    setDefaultRel(undefined);
    setDefaultParentId(null);
    setDefaultRelOverride(null);
    setDefaultRelationshipWorkflow(null);
  };

  const beginTreeAddRelativeChooser = () => {
    /*
     * Tree + / long-press / Tree quick-add all begin from a
     * closed modal and an empty parent-owned authoring context.
     */
    setAddOpen(false);
    resetAddAuthoringDefaults();
    setAddAuthoringSession(
      session => session + 1,
    );
  };

  const openAdd = (
    side: Side,
    rel?: Relationship,
    parentId?: string | null,
    customLabel?: string | null,
    relationshipWorkflow?: TreeRelationshipWorkflow | null,
  ) => {
    /*
     * Every explicit relationship selection or other Add Member
     * entry point creates a fresh modal instance. No unsaved
     * internal form state can be inherited from a prior attempt.
     */
    setAddAuthoringSession(
      session => session + 1,
    );
    setDefaultSide(side);
    setDefaultRel(rel);
    setDefaultParentId(parentId ?? null);
    setDefaultRelOverride(customLabel ?? null);
    setDefaultRelationshipWorkflow(
      relationshipWorkflow ?? null,
    );
    setAddOpen(true);
  };


  // Refresh the invites-sent count. Best-effort — silently no-ops if RLS
  // blocks the read or there's no auth session.
  const refreshInvitesSent = async () => {
    if (!user?.id) {
      setInvitesSent(0);
      return;
    }
    try {
      const { count } = await supabase
        .from('invites')
        .select('*', { count: 'exact', head: true })
        .eq('inviter_user_id', user.id);
      setInvitesSent(count ?? 0);
    } catch {
      /* non-fatal */
    }
  };

  useEffect(() => {
    refreshInvitesSent();
    const onFocus = () => refreshInvitesSent();
    window.addEventListener('focus', onFocus);
    const interval = setInterval(refreshInvitesSent, 30_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Visual unlock — when a NEW claimed member appears in the local roster
  // (after the initial bootstrap), pop a celebration toast. This covers the
  // "someone joined via invite" / "profile claimed" notification triggers.
  useEffect(() => {
    const claimed = members.filter((m) => m.profileState === 'claimed' && m.claimedByUserId);
    if (!seenIdsBootstrapped.current) {
      // First render after data loads — seed the seen set without firing.
      seenClaimedIds.current = new Set(claimed.map((m) => m.id));
      seenIdsBootstrapped.current = true;
      return;
    }
    for (const m of claimed) {
      if (!seenClaimedIds.current.has(m.id)) {
        seenClaimedIds.current.add(m.id);
        // Don't toast for the user's own self-anchor row.
        if (m.claimedByUserId === user?.id) continue;
        toast.success('Your family just grew 🎉', {
          description: `${m.name} joined KinsCircle and was added to your tree.`,
        });
      }
    }
  }, [members, user?.id]);
  useEffect(() => {
    const onOpenProfile = (event: Event) => {
      try {
        const custom = event as CustomEvent;
        const memberId = custom.detail?.memberId;

        if (!memberId) return;

        const found = members.find((m) => m.id === memberId);

        if (!found) return;

        setCenteredMemberId(memberId);
        setSelectedAction('profile');
        setSelected(found);
      } catch {}
    };

    window.addEventListener('kc:open-profile', onOpenProfile);

    return () => {
      window.removeEventListener('kc:open-profile', onOpenProfile);
    };
  }, [members]);
  /**
   * REVIEW self-anchor candidates without merging them.
   *
   * Multiple plausible YOU rows are evidence for separately governed
   * reconciliation. This effect may emit review evidence and may invoke
   * the exact owner-self claim RPC only when the database proves there is
   * one unambiguous eligible self placeholder. It never rewires ancestry,
   * moves media, deletes duplicates, or infers identity from correlation.
   */
  useEffect(() => {
    if (!user?.id) return;
    if (isInSharedGraph) return; // viewer is inside someone else's graph; nothing to merge
    if (autoMergeRunning.current) return;
    const { canonical, duplicates } = findSelfCandidates();

    if (!canonical) return;
    // Build a stable signature for this reviewed candidate set. If the same
    // evidence was already processed this session, skip duplicate reporting.
    const sig = [canonical.id, ...duplicates.map((d) => d.id).sort()].join('|');
    if (autoMergedSignatures.current.has(sig)) return;
    if (duplicates.length === 0 && canonical.claimedByUserId === user.id) {
      // Nothing to merge AND already claimed — fast path; just record sig.
      autoMergedSignatures.current.add(sig);
      return;
    }
    autoMergeRunning.current = true;
    (async () => {
      try {
        if (duplicates.length > 0) {
          await reportSelfDuplicateCandidates(
            canonical.id,
            duplicates.map((d) => d.id)
          );
        }
        if (canonical.claimedByUserId !== user.id) {
          await claimProfile(canonical.id);
        }
        // Patch a stale "Unknown" / blank canonical name with the auth name.
        const stale =
          !canonical.name ||
          !canonical.name.trim() ||
          canonical.name.trim().toLowerCase() === 'unknown';
        if (stale && youName && youName !== 'You') {
          try {
            await updateMember(canonical.id, { name: youName });
          } catch {
            /* non-fatal */
          }
        }
        autoMergedSignatures.current.add(sig);
        // Force the UI to reflect the consolidated canonical row everywhere
        // (tree, drawer, gallery, journey).
        try {
          await refresh();
        } catch {
          /* non-fatal */
        }
      } catch (err) {
        console.warn('[FamilyGraph] auto-merge failed (non-fatal)', err);
      } finally {
        autoMergeRunning.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, user?.id]);
  useEffect(() => {
    const onOpenProfile = (event: Event) => {
      try {
        const custom = event as CustomEvent;
        const memberId = custom.detail?.memberId;

        if (!memberId) return;

        const found = members.find((m) => m.id === memberId);

        if (!found) return;

        setCenteredMemberId(memberId);
        setSelectedAction('profile');
        setSelected(found);
      } catch {}
    };

    window.addEventListener('kc:open-profile', onOpenProfile);

    return () => {
      window.removeEventListener('kc:open-profile', onOpenProfile);
    };
  }, [members]);
  /**
   * Resolve the synthesized self card to a persisted owner self profile.
   *
   * Existing claimed identity is reused. An exact unclaimed owner self
   * placeholder may be bound only through claim_own_tree_self_profile(),
   * whose database authority rejects ambiguity. If no self row exists, a
   * placeholder is created first and then passed through that same server
   * claim authority. Duplicate candidates are reported for review and are
   * never automatically merged, rewired, moved, or deleted here.
   */
  /**
   * Detect duplicate self-anchor candidates without mutating Tree data.
   *
   * Duplicate identity consolidation requires reviewed, transactional
   * Governor repair authority. Ordinary profile resolution may select a
   * canonical row for display, but it must never rewire ancestry, move
   * media, or delete another row automatically.
   */
  const reportSelfDuplicateCandidates = async (
    canonicalId: string,
    duplicateIds: string[],
  ): Promise<void> => {
    const safeDuplicateIds =
      duplicateIds.filter((id) => {
        if (
          !id ||
          id === canonicalId
        ) {
          return false;
        }

        const member =
          members.find(
            candidate =>
              candidate.id === id,
          );

        if (!member) {
          return false;
        }

        const isSelfPlaceholder =
          member.side === 'self' &&
          member.ownerUserId === user?.id &&
          (
            member.relationship ===
              'unknown' ||
            (
              member.relationship as string
            ) === 'self'
          );

        const isClaimedByCurrentUser =
          member.claimedByUserId ===
          user?.id;

        return (
          isSelfPlaceholder ||
          isClaimedByCurrentUser
        );
      });

    if (
      safeDuplicateIds.length === 0
    ) {
      return;
    }

    const evidence = {
      generatedAt:
        new Date().toISOString(),
      source:
        'FamilyGraphSection',
      mode:
        'review-required',
      canonicalId,
      duplicateIds:
        safeDuplicateIds,
      mutationExecuted:
        false,
      requiredAuthority:
        'transactional-governed-identity-merge',
    };

    console.warn(
      '[FamilyGraph] duplicate self anchors require reviewed Governor repair',
      evidence,
    );

    if (
      typeof window !== 'undefined'
    ) {
      (
        window as unknown as
          Record<string, unknown>
      ).__kcTreeSelfDuplicateReview =
        evidence;

      window.dispatchEvent(
        new CustomEvent(
          'kc:tree-self-duplicate-review',
          {
            detail:
              evidence,
          },
        ),
      );
    }
  };


  const resolveSelfMember = async (): Promise<GraphMember | null> => {
    if (!user?.id) return null;

    // Gather every row that could plausibly represent the logged-in user's
    // own self-anchor — both claimed rows and any orphan side='self' rows
    // they created before the claim system existed.
    const candidates = members.filter(
      (m) =>
        m.claimedByUserId === user.id ||
        (m.side === 'self' &&
          m.ownerUserId === user.id &&
          m.relationship === 'unknown')
    );

    if (candidates.length > 0) {
      // Pick the canonical one: prefer a claimed row, then the oldest
      // (createdAt asc) so the original anchor wins.
      const sorted = [...candidates].sort((a, b) => {
        const aClaimed = a.claimedByUserId === user.id ? 0 : 1;
        const bClaimed = b.claimedByUserId === user.id ? 0 : 1;
        if (aClaimed !== bClaimed) return aClaimed - bClaimed;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
      const canonical = sorted[0];
      const dupIds = sorted.slice(1).map((m) => m.id);

      // Report duplicate identity candidates for reviewed Governor repair.
      // Profile resolution may still use the selected canonical row for this
      // session, but it never rewires or deletes Tree data automatically.
      if (dupIds.length > 0) {
        try {
          await reportSelfDuplicateCandidates(canonical.id, dupIds);
        } catch (err) {
          console.warn(
            '[FamilyGraph] duplicate review projection failed (non-fatal)',
            err,
          );
        }
      }

      // Claim the canonical row if it isn't already.
      if (canonical.claimedByUserId !== user.id) {
        await claimProfile(canonical.id);
      }

      // Optimistically patch a stale "Unknown" / blank name on the canonical
      // self row using the auth account's full name, so the YOU profile
      // never displays "Unknown" again after this point. Best-effort.
      const stale =
        !canonical.name ||
        !canonical.name.trim() ||
        canonical.name.trim().toLowerCase() === 'unknown';
      if (stale && youName && youName !== 'You') {
        try {
          await updateMember(canonical.id, { name: youName });
        } catch {
          /* non-fatal */
        }
      }

      // Refresh so the governed claimed/renamed row is what downstream
      // consumers (tree, drawer, photo gallery) observe.
      try {
        await refresh();
      } catch {
        /* non-fatal */
      }

      return {
        ...canonical,
        name: stale && youName && youName !== 'You' ? youName : canonical.name,
        profileState: 'claimed',
        claimedByUserId: user.id,
      };
    }

    // No existing self-anchor — create + claim a fresh one.
    const created = await addMember({
      name: youName,
      relationship: 'unknown',
      side: 'self',
      approvalStatus: 'approved',
      profileState: 'placeholder',
    });
    if (!created) return null;
    await claimProfile(created.id);
    try {
      await refresh();
    } catch {
      /* non-fatal */
    }
    return { ...created, profileState: 'claimed', claimedByUserId: user.id };
  };

  const handleSelectMember = async (
    m: GraphMember,
    action?: 'profile' | 'memory' | 'photo' | 'video'
  ) => {
    // Synthesized "You" tree node carries id === 'self' (not a UUID). Replace
    // it with the user's real self-anchor row before opening any drawer that
    // might write to the database.
    if (m.id === 'self') {
      if (resolvingSelf) return;
      setResolvingSelf(true);
      try {
        const real = await resolveSelfMember();
        if (real) {
          setSelectedAction(action ?? 'profile');
          setSelected(real);
        } else {
          toast.error('Could not open your profile. Please try again.');
        }
      } catch (err) {
        console.error('[FamilyGraph] resolveSelfMember failed', err);
        toast.error('Could not open your profile. Please try again.');
      } finally {
        setResolvingSelf(false);
      }
      return;
    }
    setSelectedAction(action ?? 'profile');
    const canonical = members.find((x: any) => x.id === m.id) || rosterMembers.find((x: any) => x.id === m.id) || m;
    setSelected({
      ...canonical,
      ...m,
      email: (m as any).email || (canonical as any).email || (canonical as any).authEmail || (canonical as any).auth_email || null,
      member_email: (m as any).member_email || (canonical as any).member_email || (canonical as any).authEmail || (canonical as any).auth_email || null,
      user_email: (m as any).user_email || (canonical as any).user_email || (canonical as any).authEmail || (canonical as any).auth_email || null,
    });
  };



  return (
    <section
      id="graph"
      className="py-16 bg-gradient-to-br from-[#F8F0FF] via-[#F3E8FF] to-[#FFF4D8]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="mx-auto mb-6 max-w-[260px] sm:max-w-[340px]">
            <img
              src="/brand/kinscircle-tree-icon.png"
              alt="KinsCircle Family Heritage Legacy Love"
              className="w-full h-auto rounded-3xl shadow-xl ring-1 ring-amber-200/70"
            />
          </div>
          <span className="inline-block px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 text-xs font-semibold mb-3">
            Your Family Legacy
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            Your Family's Story. Preserved Forever.
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            You're at the center. Tap any relative to open their profile —
            tap the + button to add new family members.
          </p>
        </div>

        <ProgressScore
          members={members}
          memoriesCount={memories.length}
          viewerMemberId={viewerMember?.id ?? viewerMemberId ?? null}
        />

        {/* Post-signup 3-step checklist — shown until the user has done all
            three or has explicitly dismissed it. Keeps brand-new accounts
            from going idle on the dashboard. */}
        <div className="mt-5">
          <PostSignupChecklist
            members={members}
            invitesSent={invitesSent}
            onAddParent={() => openAdd('mother', 'mother')}
            onAddOther={() => openAdd('mother')}
            onInvite={() => onInvite()}
          />
        </div>

        {/* Quick add buttons + dashboard "Share KinsCircle" — the dashboard
            screen's single, consolidated public-share entry point (the only
            other one lives in the global header). Different intent from
            "Invite family", which is the personal /invite/{user_id} flow. */}
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <Button
            onClick={() => onInvite()}
            size="sm"
            className="rounded-full bg-violet-700 text-white hover:bg-violet-800"
            title="Invite family"
          >
            <Users className="w-4 h-4 mr-1" />
            Invite Family
          </Button>
          <Button
            onClick={onShare}
            size="sm"
            variant="outline"
            className="rounded-full border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 mr-auto"
            title="Share KinsCircle publicly"
          >
            <Share2 className="w-4 h-4 mr-1" />
            Share KinsCircle
          </Button>
          <Button
            onClick={() => openAdd('mother')}
            size="sm"
            className="bg-pink-500 hover:bg-pink-600 text-white rounded-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add to Mom's side
          </Button>
          <Button
            onClick={() => openAdd('father')}
            size="sm"
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add to Dad's side
          </Button>
        </div>


        {/* Tree view filter chips — purely visual; do not modify any data.
            Lets the user focus on Mom's side, Dad's side, extended kin, or
            see the full tree. The chip "Recenter" clears any centered-on
            exploration. */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">
            Show:
          </span>
          {([
            { id: 'full', label: 'Full Tree' },
            { id: 'mother', label: "Mom's Side" },
            { id: 'father', label: "Dad's Side" },
            { id: 'extended', label: 'Extended Relatives' },
          ] as { id: TreeViewMode; label: string }[]).map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setTreeViewMode(v.id)}
              className={`text-xs font-bold rounded-full px-3 py-1.5 border transition ${
                treeViewMode === v.id
                  ? 'bg-fuchsia-600 text-white border-fuchsia-600 shadow-sm'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {v.label}
            </button>
          ))}
          {(centeredMemberId || treeViewMode !== 'full') && (
            <button
              type="button"
              onClick={() => {
                setTreeViewMode('full');
                setCenteredMemberId(null);
              }}
              className="text-xs font-bold rounded-full px-3 py-1.5 border bg-white text-fuchsia-700 border-fuchsia-300 hover:bg-fuchsia-50"
            >
              ← Recenter on Me
            </button>
          )}
        </div>
        {/* Canonical visibility banner — bloodline and modern-family
            connections are first-class Tree relationships. Only saved rows
            without a canonical viewer path remain under Needs connection. */}
        {isTreeVisibilityFiltered && (
          <div className="mt-3 rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-start gap-2.5 text-sm">
            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-emerald-900">
              <span className="font-bold">Showing your connected family.</span>{' '}
              You're seeing {canonicalTreeMembers.length} of {members.length} relatives
              on the Tree, including governed modern-family connections. Saved profiles
              without a canonical path remain available below under Needs connection.
            </div>
          </div>
        )}

        {/* Single, unified family tree */}
        <div className="mt-3">
          <FamilyTreeView
            structuralMembers={members}
            members={canonicalTreeMembers}
            canonicalGeometryRoles={
              canonicalGeometryRoles.roleByMemberId
            }
            canonicalEdges={
              canonicalViewerPresentation.edges
            }
            canonicalViewerProjections={
              viewerProjectionMap
            }
            canonicalFamilyUnitIdentity={
              canonicalFamilyUnitIdentity
            }
            canonicalBranchLayoutAuthority={
              canonicalBranchLayoutAuthority
            }
            canonicalRuntimeConvergence={
              canonicalRuntimeConvergence
            }
            canonicalSelectorDiagnostics={
              canonicalSelectorDiagnostics
            }
            canonicalSelectorDiscrepancyClassification={
              canonicalSelectorDiscrepancyClassification
            }
            canonicalSelectorDiscrepancySourceClassification={
              canonicalSelectorDiscrepancySourceClassification
            }
            authenticatedSession={
              !!user?.id
            }
            youName={youName}
            youPhotoUrl={youPhotoUrl}
            centeredMemberId={centeredMemberId}
            viewMode={treeViewMode}
            onSelectMember={(m, action) => {
              // Routes through the resolver so the synthesized "You" card
              // (id === 'self') is replaced with the real, persisted self-anchor
              // member row before any DB write touches the upload pipeline.
              handleSelectMember(m, action);
            }}

            onBeginAddRelativeChooser={
              beginTreeAddRelativeChooser
            }

            onAddRelative={(
              rel,
              side,
              _prefillName,
              parentId,
              customLabel,
              relationshipWorkflow,
            ) =>
              openAdd(
                side,
                rel,
                parentId,
                customLabel,
                relationshipWorkflow,
              )
            }


            onClearCenter={() => setCenteredMemberId(null)}
          />
        </div>



        {/* All-relatives roster — guarantees every person you've added is
            visible somewhere, even if they got placed unusually on the tree
            (e.g. side='self', shared, or filtered by the current view).
            We compute TWO sets so the roster can show three accurate
            statuses per relative:
              • Visible on tree
              • Needs connection  (truly unplaceable in full view)
              • Hidden by current view filter  (placeable, but the active
                Mom/Dad/Extended chip is filtering them out)
            Neither set ever modifies stored data — purely presentation. */}
        <div className="mt-6">
          {(() => {
            // Identify the user's canonical "YOU" anchor row — the one row
            // that IS represented by the YOU card on the tree (and is
            // legitimately not drawn as a separate node). Every OTHER row
            // that the tree's render filter would skip is a TRUE unplaced
            // row that the user can re-connect.
            //
            // FamilyTreeView's skip rule is exactly:
            //     m.side === 'self' && m.relationship === 'unknown'
            // So any non-anchor row matching that rule is invisible on the
            // tree even though it shows up in the roster. Surfacing those
            const { canonical: canonicalSelf } = findSelfCandidates();
            const canonicalSelfId = canonicalSelf?.id ?? null;

            const treeAnchor = resolveViewerAnchor(
              canonicalTreeMembers,
              viewerMember?.id ?? viewerMemberId,
            );

            /*
             * Needs connection is determined by canonical reachability.
             *
             * A valid bloodline or modern-family edge keeps a person on the
             * Tree even when the legacy GraphMember relationship is unknown.
             */
            const unplacedInFullView =
              new Set(
                canonicalViewerPresentation
                  .disconnectedMemberIds,
              );

            for (const m of rosterMembers) {
              if (m.approvalStatus === 'rejected') continue;
              if (m.id === canonicalSelfId) continue;
              if (treeAnchor && m.id === treeAnchor.id) continue;

              if (
                canonicalViewerPresentation
                  .disconnectedMemberIds
                  .has(m.id) &&
                m.side === 'self' &&
                m.relationship === 'unknown'
              ) {
                unplacedInFullView.add(m.id);
              }
            }

            // 2) Hidden-by-current-view: in non-full views, anyone who
            //    WOULD render on the full tree but is filtered out by the
            //    active Mom/Dad/Extended chip.
            const hiddenByView = new Set<string>();
            const isExtended = (m: GraphMember) =>
              m.relationship === 'aunt-uncle' ||
              m.relationship === 'cousin' ||
              m.relationship === 'cousin-child' ||
              m.relationship === 'great-grandparent';
            if (treeViewMode !== 'full') {
              for (const m of canonicalTreeMembers) {
                if (m.approvalStatus === 'rejected') continue;
                if (m.id === canonicalSelfId) continue;
                if (unplacedInFullView.has(m.id)) continue;
                let visibleOnTree = false;
                if (treeViewMode === 'mother') {
                  visibleOnTree = m.side === 'mother' || m.side === 'both';
                } else if (treeViewMode === 'father') {
                  visibleOnTree = m.side === 'father' || m.side === 'both';
                } else if (treeViewMode === 'extended') {
                  visibleOnTree = isExtended(m);
                }
                if (!visibleOnTree) hiddenByView.add(m.id);
              }
            }

            // Internal debug — silently logs counts so we can verify the
            // roster ↔ graph parity from the browser console without ever
            // surfacing UI to end users. Toggle off at any time by removing
            // this block.
            if (typeof window !== 'undefined' && (window as any).__KINS_DEBUG__) {
              // eslint-disable-next-line no-console
              console.debug('[KinsCircle] roster/graph sync', {
                totalMembers: members.length,
                treeMembers: canonicalTreeMembers.length,
                rosterMembers: rosterMembers.length,
                treeFiltered: members.length - canonicalTreeMembers.length,
                rejected: rosterMembers.filter((m) => m.approvalStatus === 'rejected').length,
                canonicalSelfId,
                unplacedInFullView: Array.from(unplacedInFullView),
                hiddenByView: Array.from(hiddenByView),
                viewMode: treeViewMode,
              });
            }

            return (
              <RelativeRoster
                members={rosterMembers}
                canonicalViewerProjections={
                  viewerProjectionMap
                }
                unplacedIds={unplacedInFullView}
                hiddenByFilterIds={hiddenByView}
                viewMode={treeViewMode}
                onView={(m) => handleSelectMember(m, 'profile')}
                onConnect={(m) => setRelationshipTarget(m)}
                onDelete={(m) =>
                  handleSelectMember(
                    m,
                    'profile',
                  )
                }
                onClearViewFilter={() => setTreeViewMode('full')}
              />
            );
          })()}
        </div>

        {/* ─── GRAPH DEBUG PANEL (DEV/STAGING STAFF ONLY) ────────────────────
            Surfaces every signal that contributes to visible placement for
            one selected person — id / name / relationship / parent_ids /
            spouse_id / spouse_partner_edges / spouse anchor / placement
            proof / unplaced membership / pyramid tier + x/y / cached-
            layout flag / duplicate records — plus a one-click
            "Repair spouse anchor" button.

            HARD VISIBILITY GATE — both conditions required:
              1. running on a deploypad.app dev/staging host
                 (DEV_BYPASS_TEST_MODE === true)
              2. the authenticated user is staff (owner / admin role)

            On kinscircleapp.com / www.kinscircleapp.com / every other
            production host the wrapper is skipped entirely so there is
            NO empty debug container, NO spacing placeholder, NO STAFF
            chip, and NO diagnostic overlay. The component itself also
            self-gates internally as a defense-in-depth backstop.

            The underlying repair engine (handleRepair / repairSpouseEdge
            / duplicate merge / cache clearing / live recompute) is NOT
            removed — it remains fully functional and reachable for
            staff on the dev host. Only the UI exposure is suppressed
            on production. */}
        {DEV_BYPASS_TEST_MODE && isStaff && (
          <div className="mt-6">
            <GraphDebugPanel />
          </div>
        )}



        {/* Missing-branch prompts — explicit "Add your mother / Invite your
            cousin" cards based on detected gaps. Auto-hides when the tree
            is reasonably complete. */}

        <div className="mt-6">
          <MissingBranches
            members={members}
            onAdd={({ relationship, side }) => openAdd(side, relationship)}
            onInvite={(opts) =>
              onInvite({ relation: opts?.relation ?? null, personName: opts?.relName ?? null })
            }
          />
        </div>


        {/* Smart suggestions */}
        <div className="mt-6">
          <SmartSuggestions
            members={members}
            onSelect={({ relationship, side }) => openAdd(side, relationship)}
          />
        </div>

        {/* Achievements — small badges (max 6) computed from existing counts. */}
        <div className="mt-6">
          <FamilyAchievements
            members={members}
            memoriesCount={memories.length}
            invitesSent={invitesSent}
          />
        </div>

        {/* Missions + Approvals */}
        <div className="mt-8 grid lg:grid-cols-2 gap-5">
          <Missions
            members={members}
            memoriesCount={memories.length}
            onAddSide={openAdd}
            onInvite={onInvite}
          />
          {isStaff ? (
            <div className="rounded-2xl bg-white border-2 border-amber-200 p-5">
              <h3 className="font-serif font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-600" />
                Approval queue
              </h3>
              <PendingApprovalsPanel
                members={members}
                canonicalViewerProjections={
                  viewerProjectionMap
                }
              />
            </div>
          ) : (
            <div className="rounded-2xl bg-white border-2 border-fuchsia-100 p-5">
              <h3 className="font-serif font-bold text-lg text-slate-900 mb-2 flex items-center gap-2">
                <Heart className="w-5 h-5 text-fuchsia-600" />
                How it works
              </h3>
              <ul className="text-sm text-slate-700 space-y-2">
                <li>• Tap any relative card to open their full profile, photos, and memories.</li>
                <li>• Tap the small + button on a card (or long-press it) to add a relative connected to that person.</li>
                <li>
                  • Approved relationships get a{' '}
                  <ShieldCheck className="inline w-3.5 h-3.5 text-emerald-600" /> verified badge after 2 confirmations.
                </li>
                <li>• From any profile, use "Center tree on this person" to explore their branch of the family.</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <AddGraphMemberModal
        key={`add-authoring-${addAuthoringSession}`}
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open);
          // Clear the tapped-node parent context as soon as the modal
          // closes so the NEXT add (e.g. from a global "Add to Mom's
          // side" button) starts with a fresh, empty parent picker
          // and doesn't reuse the previously-tapped node by accident.
          if (!open) {
            resetAddAuthoringDefaults();

            /*
             * Discard all unsaved modal-local fields as well as
             * parent-owned defaults after cancellation/close.
             */
            setAddAuthoringSession(
              session => session + 1,
            );
          }
        }}
        defaultSide={defaultSide}
        defaultRelationship={defaultRel}
        defaultParentId={defaultParentId}
        // FORWARD the custom display label collected from the quick-add
        // menu (Grandchild / Great-Aunt-Uncle / Niece-Nephew / 2nd Cousin /
        // Step-Mom / In-laws / Godmother / Family Friend / etc.) into the
        // modal so its dropdown preselects the matching extended option
        // and the visible label is preserved verbatim — no more "I picked
        // Grandchild but the modal shows Child" mismatch.
        defaultRelationshipOverride={defaultRelOverride}
        defaultRelationshipWorkflow={
          defaultRelationshipWorkflow
        }
      />


      {/* MEDIA / PROFILE BLANK-PAGE BUG FIX —
          Wrap the profile drawer in an ErrorBoundary so a render error
          inside the drawer (e.g. a malformed media row, a stale URL, a
          crashing comment thread) shows a "This memory could not be
          loaded." card and lets the user close back to the tree, instead
          of blanking the entire page. The boundary's onReset clears the
          selected member so the next tap on a card starts fresh.

          Pure presentation guard — does NOT touch parent/child edges,
          spouse links, kinship engine, addRelativeUniversal,
          FamilyGraphContext write paths, invites, Stripe, schema or
          RLS. */}
      <ErrorBoundary
        label="Profile"
        fallbackMessage="The profile or its photos / videos could not be loaded. Please close and try again."
        onReset={() => {
          setSelected(null);
          setSelectedAction(undefined);
        }}
      >
        <ProfileDetailDrawer
          member={selected}
          canonicalViewerProjections={
            viewerProjectionMap
          }
          canonicalRelationshipEdges={
            canonicalViewerPresentation.edges
          }
          initialAction={selectedAction}
          onClose={() => {
            setSelected(null);
            setSelectedAction(undefined);
          }}
          isStaff={isStaff}
          youName={youName}
          onAddRelative={({ relationship, side }) => openAdd(side, relationship)}
          onCenterOnMember={(m) => {
            setCenteredMemberId(m.id);
            setSelected(null);
            setSelectedAction(undefined);
          }}
          onInvitePerson={(m) =>
            onInvite({ personName: m.name, relation: m.relationship, targetMemberId: m.id })
          }
          onEditRelationship={(m) => {
            setSelected(null);
            setSelectedAction(undefined);
            setRelationshipTarget(m);
          }}
          onMessagePerson={(m) => {
            setSelected(null);
            setSelectedAction(undefined);
            window.dispatchEvent(new CustomEvent('kinscircle:open-messages', {
              detail: {
              memberId: m.id,
              memberName: m.name,
              email:
                (m as any).authEmail ||
                (m as any).auth_email ||
                (m as any).email ||
                null,
            }
            }));
          }}
        />
      </ErrorBoundary>


      {/* Lightweight modal that lets the user re-place a relative on the
          tree (change relationship + side). Opened from the RelativeRoster's
          "Connect to Tree" / "Set Relationship" buttons AND from the
          ProfileDetailDrawer's "Edit Relationship" button. Calls existing
          updateMember + linkParents — no schema changes, no data loss. */}
      <SetRelationshipModal
        open={!!relationshipTarget}
        onOpenChange={(open) => {
          if (!open) setRelationshipTarget(null);
        }}
        member={relationshipTarget}
      />


    </section>
  );
};



interface Props {
  onInvite: InviteHandler;
  onShare: () => void;
}

// ── PROVIDER-SCOPE FIX (Nov 2026) ─────────────────────────────────────
// Previously this component wrapped <Inner /> in its OWN nested
// <FamilyGraphProvider>, creating a SECOND state island that the
// AppLayout-level provider (consumed by Header → SystemStatusPanel and
// any other top-level reader) could not see. Result: the visible tree
// rendered members from the inner provider while SystemStatusPanel read
// the outer provider and reported members.length=0 / bindingState=loading.
//
// Fix: the FamilyGraphProvider is now mounted ONCE at the AppLayout
// shell level, so every consumer (Header, SystemStatusPanel, the tree,
// FamilyActivityFeed, AI cards) reads from the SAME provider instance.
// This file no longer wraps its children in a second provider — it
// just renders <Inner /> against the shared context.
const FamilyGraphSection: React.FC<Props> = ({ onInvite, onShare }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <SignedOutPlaceholder />;
  return <Inner onInvite={onInvite} onShare={onShare} />;
};

export default FamilyGraphSection;

